import { Component, OnInit } from '@angular/core';
import { AttritionService } from '../../../services/attrition.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { CardComponent, CardBodyComponent, CardHeaderComponent, RowComponent, ColComponent } from '@coreui/angular';
import { ChartjsModule, ChartjsComponent } from '@coreui/angular-chartjs';

export interface RetentionRow {
  month: string;
  start_count: number | string;
  end_count: number | string;
  new_hires: number | string;
  retention_rate: number | string;
  replacement_count: number | string;
  resigned: number | string;
  awol: number | string;
  terminated: number | string;
  endo: number | string;
}

const DS_CONFIGS = [
  { label: 'Employees End',  borderColor: '#0d6efd', lineBg: 'rgba(13,110,253,0.15)',  barBg: 'rgba(13,110,253,0.75)'  },
  { label: 'New Hires',      borderColor: '#20c997', lineBg: 'rgba(32,201,151,0.15)',  barBg: 'rgba(32,201,151,0.75)'  },
  { label: 'Total Attrition',borderColor: '#e55353', lineBg: 'rgba(229,83,83,0.12)',   barBg: 'rgba(229,83,83,0.75)'   }
];

@Component({
  selector: 'app-retention-data',
  standalone: true,
  imports: [CommonModule, FormsModule, CardComponent, CardBodyComponent, CardHeaderComponent, RowComponent, ColComponent, ChartjsModule, ChartjsComponent],
  templateUrl: './retention-data.component.html',
  styleUrls: ['./retention-data.component.scss']
})
export class RetentionDataComponent implements OnInit {
  readonly currentYear = new Date().getFullYear();
  readonly monthNames = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  readonly dsConfigs = DS_CONFIGS;

  year = this.currentYear;
  retentionRows: RetentionRow[] = [];

  chartType: 'bar' | 'line' = 'line';
  datasetVisibility = [true, true, true];
  showChart = true;

  private chartInstance: any = null;
  // Raw series arrays, kept in sync with loaded data
  private seriesData: (number | null)[][] = [[], [], []];

  chartData: any = { labels: this.monthNames, datasets: this.buildDatasets([[], [], []]) };

  chartOptions: any = {
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: 'Yearly Retention Overview',
        color: '#212529',
        font: { size: 17, weight: 'bold' },
        padding: { bottom: 16 }
      },
      tooltip: {
        backgroundColor: 'rgba(33,37,41,0.92)',
        titleColor: '#fff',
        bodyColor: '#e9ecef',
        borderColor: 'rgba(255,255,255,0.08)',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6c757d', font: { size: 12 } },
        border: { display: false }
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { color: '#6c757d', font: { size: 12 } },
        border: { display: false }
      }
    }
  };

  constructor(private attritionService: AttritionService) {}

  ngOnInit(): void {
    this.getData();
  }

  /** Build dataset configs based on current chartType and provided series arrays */
  private buildDatasets(series: (number | null)[][]): any[] {
    const isLine = this.chartType === 'line';
    return DS_CONFIGS.map((cfg, i) => ({
      label: cfg.label,
      data: series[i] ?? [],
      backgroundColor: isLine ? cfg.lineBg : cfg.barBg,
      borderColor: cfg.borderColor,
      borderWidth: 2.5,
      pointBackgroundColor: cfg.borderColor,
      pointBorderColor: '#fff',
      pointRadius: isLine ? 5 : 0,
      pointHoverRadius: isLine ? 7 : 0,
      fill: isLine,
      tension: isLine ? 0.4 : 0
    }));
  }

  /** Called by (chartRef) output when Chart.js instance is created/recreated */
  onChartRef(chart: any): void {
    this.chartInstance = chart;
    // Restore visibility state on every creation
    this.datasetVisibility.forEach((visible, i) => {
      this.chartInstance.setDatasetVisibility(i, visible);
    });
    this.chartInstance.update();
  }

  /** Toggle chart type — must recreate chart because Chart.js doesn't support runtime type changes */
  setChartType(type: 'bar' | 'line'): void {
    if (this.chartType === type) return;
    this.chartType = type;
    this.chartData = { labels: this.monthNames, datasets: this.buildDatasets(this.seriesData) };
    // Destroy and recreate the chart component via *ngIf
    this.showChart = false;
    setTimeout(() => { this.showChart = true; });
  }

  /** Toggle a dataset — uses Chart.js instance API directly, no chart recreation needed */
  toggleDataset(idx: number): void {
    this.datasetVisibility[idx] = !this.datasetVisibility[idx];
    if (this.chartInstance) {
      this.chartInstance.setDatasetVisibility(idx, this.datasetVisibility[idx]);
      this.chartInstance.update();
    }
  }

  getData(): void {
    if (!this.year) return;
    this.attritionService.retentionData({ year: this.year }).subscribe({
      next: (data) => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth(); // 0-based

        interface MappedRow extends RetentionRow { monthIndex: number; }
        const allRows: MappedRow[] = (data ?? []).map((row: any) => {
          const monthIndex = Number(row.month) - 1;
          return {
            month: this.monthNames[monthIndex] ?? String(row.month),
            monthIndex,
            start_count: row.start_count,
            end_count: row.end_count,
            new_hires: row.new_hires,
            retention_rate: row.retention_rate,
            replacement_count: row.replacement_count,
            resigned: row.resigned,
            awol: row.awol,
            terminated: row.terminated,
            endo: row.endo
          };
        });

        // Table: exclude future months for the current year
        this.retentionRows = this.year === currentYear
          ? allRows.filter(r => r.monthIndex <= currentMonth)
          : allRows;

        // Chart: all 12 labels, null for future months in current year
        const rowByIndex = new Map(allRows.map(r => [r.monthIndex, r]));
        const seriesEnd: (number | null)[] = [];
        const seriesHires: (number | null)[] = [];
        const seriesAttrition: (number | null)[] = [];

        this.monthNames.forEach((_, idx) => {
          const r = rowByIndex.get(idx);
          if (r && (this.year !== currentYear || idx <= currentMonth)) {
            seriesEnd.push(typeof r.end_count === 'number' ? r.end_count : 0);
            seriesHires.push(typeof r.new_hires === 'number' ? r.new_hires : 0);
            const attrition = [r.resigned, r.awol, r.terminated, r.endo]
              .map(v => typeof v === 'number' ? v : 0)
              .reduce((a, b) => a + b, 0);
            seriesAttrition.push(attrition);
          } else {
            seriesEnd.push(null);
            seriesHires.push(null);
            seriesAttrition.push(null);
          }
        });

        this.seriesData = [seriesEnd, seriesHires, seriesAttrition];
        this.chartData = { labels: this.monthNames, datasets: this.buildDatasets(this.seriesData) };

        // After data is set, restore visibility state via the Chart.js API
        if (this.chartInstance) {
          setTimeout(() => {
            this.datasetVisibility.forEach((visible, i) => {
              this.chartInstance.setDatasetVisibility(i, visible);
            });
            this.chartInstance.update();
          });
        }
      },
      error: (err) => console.error('Failed to fetch retention data', err)
    });
  }

  onYearChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const year = Number(input.value);
    if (!isNaN(year) && year >= 2025 && year <= this.currentYear) {
      this.year = year;
      this.getData();
    }
  }
}
